import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Plus, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * ExplorarRutas — a clean catalog of ready-to-start Ruta templates.
 *
 * This is DIFFERENT from /mis-rutas. Mis Rutas shows only the entrepreneur's
 * own active work; Explorar is a browsing surface for template blueprints
 * (Marca de Ropa, Tienda Online, Salón de Belleza, Consultoría, etc.).
 *
 * The two pages never mix — one is "my workspace", the other is "the catalog".
 */
export default function ExplorarRutas() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/playbooks/templates")
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => toast.error("No se pudieron cargar las rutas"))
      .finally(() => setLoading(false));
  }, []);

  const iniciar = async (name) => {
    setStarting(name);
    try {
      const { data } = await api.post("/playbooks/from-template", { name });
      toast.success(`Ruta iniciada: ${name}`);
      // Make the freshly-created ruta the active one so the whole app pivots to it.
      try { await api.put("/settings", { active_playbook_id: data.id }); } catch {}
      nav(`/playbooks/${data.id}`);
    } catch {
      toast.error("No se pudo iniciar la ruta");
    } finally {
      setStarting(null);
    }
  };

  const filtered = q
    ? templates.filter((t) =>
        t.name.toLowerCase().includes(q.toLowerCase()) ||
        (t.category || "").toLowerCase().includes(q.toLowerCase())
      )
    : templates;

  return (
    <div data-testid="page-explorar">
      <header className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <div className="label-mono text-muted-foreground">Explorar Rutas</div>
          <h1 className="display text-4xl sm:text-5xl md:text-6xl mt-1 leading-[0.95]">
            Catálogo de rutas listas
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Elige una ruta base para empezar. Al iniciarla se convierte en tu ruta activa —
            podrás editar etapas, agregar hitos y adaptar todo a tu negocio.
          </p>
        </div>
        <Link
          to="/brujula"
          className="btn-secondary flex items-center gap-2 self-start md:self-auto"
          data-testid="btn-brujula-desde-explorar"
        >
          <Compass size={14} weight="bold" /> ¿No sabes cuál elegir? Ir a Brújula
        </Link>
      </header>

      <div className="mb-6 relative max-w-md">
        <MagnifyingGlass
          size={16}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar ruta o categoría…"
          className="input-brut !pl-10 w-full"
          data-testid="input-explorar-search"
        />
      </div>

      {loading && <div className="text-sm text-muted-foreground">Cargando rutas…</div>}

      {!loading && filtered.length === 0 && q && (
        <div className="brut-card" data-testid="explorar-empty">
          <div className="label-mono text-muted-foreground">Sin resultados</div>
          <p className="mt-2 text-sm">
            No encontramos rutas que coincidan con “{q}”. Prueba con otra búsqueda o inicia una nueva
            ruta desde la Brújula.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((t) => {
          const testId = `explorar-tpl-${t.name.replace(/\s+/g, "-").toLowerCase()}`;
          return (
            <article
              key={t.name}
              className="brut-card flex flex-col justify-between"
              data-testid={testId}
            >
              <div>
                <div className="label-mono text-muted-foreground">{t.category}</div>
                <h3 className="display text-2xl mt-1 leading-tight break-words">{t.name}</h3>
                <div className="mt-3 text-xs text-muted-foreground">
                  {t.stages_count} etapa{t.stages_count === 1 ? "" : "s"} · flujo guiado paso a paso
                </div>
              </div>
              <button
                type="button"
                onClick={() => iniciar(t.name)}
                disabled={starting === t.name}
                className="mt-5 btn-primary w-full flex items-center justify-center gap-2"
                data-testid={`btn-iniciar-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {starting === t.name ? (
                  "Iniciando…"
                ) : (
                  <>
                    <Plus size={14} weight="bold" /> Iniciar esta ruta
                    <ArrowRight size={12} weight="bold" />
                  </>
                )}
              </button>
            </article>
          );
        })}
      </div>
    </div>
  );
}
